/**
 * The signed-in client, stored under the current site's keys so a user signed in on the old site
 * stays signed in on the new one (docs/legacy-audit/STORAGE.md L1/L2):
 *   localStorage['jwt_token']   the raw JWT (not JSON)
 *   localStorage['auth_client'] the client object, JSON, login shape (`clientId`, `clientname`, …)
 * A token whose `exp` has passed counts as signed out (and is cleared, like the old site's boot).
 *
 * Expiry (CK-02, AU-02, STORAGE §2):
 *   at boot          `dropExpiredSession()` clears a token that is already expired, silently: the visitor
 *                    starts signed out, on the same page (the old site's boot);
 *   during the visit an expired token found before an authed call (`readToken`), or a 401 answered to a
 *                    request that carried the token (`expireSession`, called by the http client), ends the
 *                    session: storage is cleared, the listeners (`onSessionExpired`: the shell resets the
 *                    session query and opens the sign-in page with `?session=expired&redirect=`) are told
 *                    once, and until someone signs in again no call made in the user's name goes out as a
 *                    guest's (`sessionEnded`).
 */
import { readRaw, removeRaw, writeRaw } from '../storage';

export const TOKEN_KEY = 'jwt_token';
export const CLIENT_KEY = 'auth_client';
/** L3: the client object of older builds of the old site (personal data): never written, removed at boot and at every logout. */
export const LEGACY_CLIENT_KEY = 'client';

export type StoredClient = Record<string, unknown> & { clientId?: string; _id?: string };

/** `exp` (seconds) of a JWT, or null when it has none / cannot be read. */
export function jwtExpiry(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='))) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

export function isExpired(token: string, now = Date.now()): boolean {
  if (token.split('.').length !== 3) return true;
  const exp = jwtExpiry(token);
  return exp !== null && exp * 1000 <= now;
}

/** The stored token when present and not expired; an expired one ends the session (`expireSession`). */
export function readToken(): string | null {
  const token = readRaw(TOKEN_KEY);
  if (!token) return null;
  if (isExpired(token)) {
    expireSession();
    return null;
  }
  return token;
}

/* ── Expiry ──────────────────────────────────────────────────────────── */

/** The session of this visit ended by itself (expired or refused token) and nobody has signed in since. */
let ended = false;
const listeners = new Set<() => void>();

/** True once the session expired during this visit, until the next sign-in or sign-out. */
export const sessionEnded = (): boolean => ended;

/**
 * The session is over (expired token, or a 401 to a request that carried it): L1, L2 and L3 are removed and
 * the listeners are told, once per session (several requests failing together give one notice).
 */
export function expireSession(): void {
  clearSession();
  if (ended) return;
  ended = true;
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch (e) {
      console.error('[session] expiry listener failed', e);
    }
  }
}

/** Called when the session expires (the shell: signed-out interface, sign-in page). Returns the unsubscribe. */
export function onSessionExpired(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Boot (AU-01, before any request): a token that is already expired is dropped silently, nothing is told. */
export function dropExpiredSession(): void {
  const token = readRaw(TOKEN_KEY);
  if (token && isExpired(token)) clearSession();
}

export function readClient(): StoredClient | null {
  const raw = readRaw(CLIENT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as StoredClient) : null;
  } catch {
    return null;
  }
}

/**
 * Stores the session. The client is merged over the previous one with both identity keys kept equal
 * (`clientId` and `_id`), as the old site's `mergeClient` did; `clientName` (register response)
 * is normalised to `clientname` (login response).
 */
export function saveSession(token: string | null, client: Record<string, unknown> | null): void {
  if (token) {
    writeRaw(TOKEN_KEY, token);
    ended = false; // signed in again
  }
  if (client) {
    const prev = readClient() ?? {};
    const { clientName, ...rest } = client;
    const merged: StoredClient = { ...prev, ...rest };
    if (clientName !== undefined && rest.clientname === undefined) merged.clientname = clientName;
    const id = String(merged.clientId ?? merged._id ?? '');
    if (id) {
      merged.clientId = id;
      merged._id = id;
    }
    writeRaw(CLIENT_KEY, JSON.stringify(merged));
  }
}

/** Removes the session keys: L1, L2 and the legacy L3 (every logout path, STORAGE §2). Other keys are kept. */
export function clearSession(): void {
  removeRaw(TOKEN_KEY);
  removeRaw(CLIENT_KEY);
  removeRaw(LEGACY_CLIENT_KEY);
}

/** The user's own sign-out: the session keys go and nothing counts as expired any more. */
export function signOut(): void {
  clearSession();
  ended = false;
}
